import { api } from '../../api/client';
import { Banner, Card, Empty, formatDate } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../hooks/useAuth';

/**
 * Lecture seule, pour tout le monde (brief §16).
 *
 * Seuls les plannings **publiés** apparaissent : un brouillon n'engage
 * personne, et l'afficher ferait venir quelqu'un pour rien.
 */
export default function PublishedScreen() {
  const { isManager } = useAuth();
  const mine = useAsync(() => api.myAssignments(), []);
  // `GET /planning/runs` est réservé aux responsables : conditionner le rendu
  // ne suffit pas, il faut conditionner la **requête**. Sans cela un membre
  // déclenche un 403 à chaque affichage de son propre planning.
  const published = useAsync(
    () => (isManager ? api.runs('PUBLISHED') : Promise.resolve([])),
    [isManager],
  );
  const latest = published.data?.[0] ?? null;
  const detail = useAsync(
    () => (latest === null ? Promise.resolve(null) : api.run(latest.id)),
    [latest?.id],
  );

  return (
    <>
      <h1>Planning</h1>

      {mine.error && <Banner kind="error">{mine.error}</Banner>}

      <Card title="Mes prochains services">
        {mine.loading ? (
          <p className="muted">Chargement…</p>
        ) : (mine.data ?? []).length === 0 ? (
          <Empty>
            Vous n'avez aucun service prévu dans un planning publié.
          </Empty>
        ) : (
          <div className="stack">
            {(mine.data ?? []).map((row) => (
              <div key={`${row.event_id}-${row.role_id}`} className="spread">
                <span>
                  <strong>{formatDate(row.starts_at)}</strong>
                  <br />
                  <span className="muted">{row.event_label}</span>
                </span>
                <span className="slot">
                  <span className="chip">{row.role_name}</span>
                  {row.is_locked && <span className="badge locked">fixé</span>}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {isManager && (
        <Card title="Dernier planning publié">
          {published.loading || detail.loading ? (
            <p className="muted">Chargement…</p>
          ) : detail.data === null ? (
            <Empty>Aucun planning n'a encore été publié.</Empty>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Personne</th>
                    <th>Services</th>
                    <th>Disponibilités</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.data.per_person ?? [])
                    .filter((person) => person.capacity > 0)
                    .map((person) => (
                      <tr key={person.person_id}>
                        <td>{person.name}</td>
                        <td>{person.services}</td>
                        <td>
                          <span className="muted">
                            {person.services} / {person.capacity}
                          </span>
                          <div className="meter" aria-hidden="true">
                            <span
                              style={{
                                width: `${Math.round(
                                  (person.services / Math.max(1, person.capacity)) * 100,
                                )}%`,
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
